import type { paths } from "../openapi/schema";

type PatientMedicationPatchBody = NonNullable<
  paths["/{tenantId}/patients/{patientId}/medications/{medicationId}"]["patch"]["requestBody"]
>["content"]["application/json"];

/** Canonical administration-route values accepted by Dashboard API. */
export type PatientMedicationRoute = NonNullable<
  PatientMedicationPatchBody["route"]
>;

export const PATIENT_MEDICATION_ROUTES = [
  "oral",
  "subcutaneous",
  "intramuscular",
  "intravaginal",
  "transdermal",
  "intranasal",
  "sublingual",
  "topical",
  "intravenous",
  "other",
] as const satisfies readonly PatientMedicationRoute[];

const PATIENT_MEDICATION_ROUTE_SET = new Set<string>(PATIENT_MEDICATION_ROUTES);

const EDITOR_TO_API_ROUTE: Record<string, PatientMedicationRoute> = {
  vaginal: "intravaginal",
  patch: "transdermal",
  nasal: "intranasal",
};

const API_TO_EDITOR_ROUTE: Partial<Record<PatientMedicationRoute, string>> = {
  intravaginal: "vaginal",
  transdermal: "patch",
  intranasal: "nasal",
};

/**
 * Convert patient-facing editor values to the canonical API enum.
 * Empty and unsupported values deliberately serialize as null so clearing a
 * route is explicit and an arbitrary OCR/catalog string cannot reach the API.
 */
export function serializeMedicationRoute(
  value: string | null | undefined,
): PatientMedicationRoute | null {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return null;

  const apiValue = EDITOR_TO_API_ROUTE[normalized] ?? normalized;
  return PATIENT_MEDICATION_ROUTE_SET.has(apiValue)
    ? (apiValue as PatientMedicationRoute)
    : null;
}

/**
 * Convert a persisted API/catalog route back to the labels used by the editor.
 * Values that already use the patient-facing spelling are preserved so local
 * content defaults and legacy fixtures continue to prefill correctly.
 */
export function deserializeMedicationRoute(
  value: string | null | undefined,
): string {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "";

  if (EDITOR_TO_API_ROUTE[normalized]) return normalized;
  if (!PATIENT_MEDICATION_ROUTE_SET.has(normalized)) return "";

  return (
    API_TO_EDITOR_ROUTE[normalized as PatientMedicationRoute] ?? normalized
  );
}
